import React, { useState, useEffect } from 'react'
import { Modal, Button, Space, Typography } from 'antd'
import { DownloadOutlined, CloseOutlined } from '@ant-design/icons'

const { Text, Title } = Typography

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallModal, setShowInstallModal] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    // 检查是否已在 PWA 模式下运行
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setIsStandalone(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // 用户访问过几次后再提示安装
      const visits = parseInt(localStorage.getItem('novelai_visits') || '0')
      localStorage.setItem('novelai_visits', String(visits + 1))
      if (visits >= 2) {
        setTimeout(() => setShowInstallModal(true), 30000) // 30秒后弹出
      }
    }

    window.addEventListener('beforeinstallprompt', handler)

    // 检测 PWA 安装状态变化
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null)
      setShowInstallModal(false)
      setIsStandalone(true)
    })

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setDeferredPrompt(null)
    }
    setShowInstallModal(false)
  }

  // 已安装或不在支持环境中
  if (isStandalone || !deferredPrompt) return null

  return (
    <Modal
      open={showInstallModal}
      onCancel={() => setShowInstallModal(false)}
      footer={null}
      closable={false}
      centered
      width={360}
      className="pwa-install-modal"
    >
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <div style={{
          width: 72,
          height: 72,
          borderRadius: 16,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          fontSize: 36,
          fontWeight: 'bold',
          color: '#fff',
        }}>
          N
        </div>
        <Title level={4} style={{ margin: '0 0 8px' }}>
          安装 NovelAI
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          安装到桌面，获得更快更流畅的创作体验
        </Text>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="primary"
            size="large"
            icon={<DownloadOutlined />}
            block
            onClick={handleInstall}
            style={{ height: 44, borderRadius: 12, fontSize: 16 }}
          >
            安装应用
          </Button>
          <Button
            size="large"
            icon={<CloseOutlined />}
            block
            onClick={() => setShowInstallModal(false)}
            style={{ height: 44, borderRadius: 12 }}
          >
            稍后再说
          </Button>
        </Space>
      </div>
    </Modal>
  )
}

export default InstallPrompt
